// @ts-nocheck TODO

import {html, LitElement} from "lit";
import {classMap} from "lit/directives/class-map.js";
import {customElement, property, query, state} from "lit/decorators.js";
import {dropdownItemStyles} from "./DropdownItem.styles.js";

import "./IconButton.js";

const DROPDOWN_ITEM_CLASS = "dropdown-item";
const DROPDOWN_ITEM_SELECTOR = `.${DROPDOWN_ITEM_CLASS}`;
const FOCUS_WITHIN_CLASS = "--focus-within";

@customElement("pn-dropdown-item")
export class DropdownItem extends LitElement {
	@property() classNames: string;
	@property() onClick:    () => void;
	@property() href:       string;
	@property() type:       "link" | "button" | "submenu" = "link";

	@state() isFocusedWithin = false;
	@state() isTouched = false;

	@query(DROPDOWN_ITEM_SELECTOR) _dropdownItem: HTMLElement;
	@query("slot[name=submenu]") _submenuSlot:    HTMLSlotElement;

	static styles = [dropdownItemStyles];

	connectedCallback() {
		super.connectedCallback();
		this.setAttribute("role", "menuitem");
	}

	getDropdownDiv() {
		return this._submenuSlot.assignedElements()[0] as HTMLElement;
	}

	removeFocusWithin = () => {
		this.isFocusedWithin = false;
		// For some reason the classmap doesn't respond in this case
		this.renderRoot.querySelector(DROPDOWN_ITEM_SELECTOR)?.classList.remove(FOCUS_WITHIN_CLASS);
	};

	onKeyDownDropdownItem = (event: KeyboardEvent) => {
		const key = event.key;

		// Only continue on arrow keys
		if (key !== "ArrowLeft" && key !== "ArrowUp" && key !== "ArrowRight" && key !== "ArrowDown" && key !== "Escape")
			return;

		event.preventDefault();
		event.stopPropagation();

		switch (key) {
			case "ArrowDown":
				this.nextElementSibling?.shadowRoot?.querySelector(DROPDOWN_ITEM_SELECTOR)?.focus();
				break;

			case "ArrowLeft": {
				const parentElement = this.parentElement;
				if (parentElement?.nodeName !== "PN-DROPDOWN-SUBMENU")
					break;

				// Focus parent item
				const parentItem = parentElement?.parentElement?.shadowRoot?.querySelector(DROPDOWN_ITEM_SELECTOR);
				(parentItem as HTMLElement)?.focus();
				break;
			}

			case "ArrowRight": {
				if (!this._submenuSlot)
					break;

				// Keep submenu visible
				this.isFocusedWithin = true;
				const dropdownDiv = this.getDropdownDiv();

				// Focus first item in submenu
				const firstItem = dropdownDiv?.querySelector("pn-dropdown-item")?.shadowRoot?.querySelector(DROPDOWN_ITEM_SELECTOR);

				// TODO: This only seems to work with a timeout
				setTimeout(() => {
					(firstItem as HTMLElement)?.focus();
				}, 0);
				break;
			}

			case "ArrowUp":
				this.previousElementSibling?.shadowRoot?.querySelector(DROPDOWN_ITEM_SELECTOR)?.focus();
				break;

			case "Escape": {
				// Simulate a click on the body to close the dropdown
				document.body.click();

				this.removeFocusWithin();

				const myEvent = new CustomEvent("dropdownRemoveFocus", {
					bubbles:  true,
					composed: true
				});
				this.dispatchEvent(myEvent);

				break;
			}
		}
	};

	positionSubmenu = (event) => {
		this.positionSubmenuTopAndLeft(event);
		this.positionSubmenuOffScreen();
	};

	positionSubmenuTopAndLeft = (event) => {
		const dropdownDiv = this.getDropdownDiv();
		// Parent container of the current dropdown item list
		const currentList = this.parentNode.shadowRoot.querySelector(".dropdown-menu");

		// Set the y position
		const yPos = this.getBoundingClientRect().top - currentList.getBoundingClientRect().top;
		dropdownDiv.style.top = yPos + "px";

		/**
		 * Set the x position
		 *
		 * If the current list has a scrollbar, place the submenu closer to
		 * cover the scrollbar, so that users can use a mouse seamlessly without
		 * the submenu closing before the mouse reaches it.
		 *
		 * Compare dropdown item width with dropdown menu/list width to
		 * determine if there's a scroll bar
		 */
		if (currentList?.offsetWidth > this._dropdownItem.offsetWidth)
			dropdownDiv.style.left = "calc(100% - 8px)";
	};

	positionSubmenuOffScreen = () => {
		const dropdownDiv = this.getDropdownDiv();
		const dropdownBounds = dropdownDiv.getBoundingClientRect();

		if (dropdownBounds.y < 0) {
			// Ran off top edge
			dropdownDiv.style.top = (0 - dropdownBounds.y) + "px";
		} else if (dropdownBounds.bottom > document.documentElement.clientHeight) {
			// Ran off bottom edge
			const hiddenHeight = dropdownBounds.bottom - document.documentElement.clientHeight;
			dropdownDiv.style.top = (parseFloat(dropdownDiv.style.top) - hiddenHeight) + "px";
		}

		if (dropdownBounds.right > document.documentElement.clientWidth) {
			// Ran off right edge
			const hiddenWidth = dropdownBounds.right - document.documentElement.clientWidth;
			if (hiddenWidth < 50) {
				// Only slightly off screen, shift it over so it's not off screen
				const dropdownLeft = parseFloat(window.getComputedStyle(dropdownDiv).getPropertyValue("left"));
				dropdownDiv.style.left = (dropdownLeft - hiddenWidth) + "px";
			} else {
				// Move it to the other side of the current dropdown menu
				dropdownDiv.style.left = (15 - dropdownBounds.width) + "px";
			}
		}
	};

	resetSubmenuPosition = (event) => {
		// The new target is a submenu, so don't change its position
		// TODO: Also don't do this if this is still hovered or focused
		if (this.contains(event.relatedTarget))
			return;

		// Close the submenu
		this.isFocusedWithin = false;

		const dropdownDiv = this.getDropdownDiv();
		dropdownDiv.style.removeProperty("top");
		dropdownDiv.style.removeProperty("left");
	};

	render() {
		switch (this.type) {
			case "link": {
				return html`
					<a
						class=${classMap({
							[DROPDOWN_ITEM_CLASS]: true,
							[this.classNames]:     this.classNames !== undefined
						})}
						@keydown="${this.onKeyDownDropdownItem}"
						href="${this.href}"
						rel="noreferrer"
						target="_blank"
					>
						<slot></slot>
					</a>
				`;
			}

			case "button": {
				return html`
					<button
						class="${DROPDOWN_ITEM_CLASS}"
						@click=${this.onClick}
						@keydown="${this.onKeyDownDropdownItem}"
						type="button"
					>
						<slot></slot>
					</button>
				`;
			}

			case "submenu": {
				return html`
					<div
						class="dropdown-item-wrapper"
						@dropdownRemoveFocus=${this.removeFocusWithin}
					>
						<div
							class="${classMap({
								[DROPDOWN_ITEM_CLASS]: true,
								[FOCUS_WITHIN_CLASS]:  this.isFocusedWithin
							})}"
							@blur="${this.resetSubmenuPosition}"
							@focus="${this.positionSubmenu}"
							@keydown="${this.onKeyDownDropdownItem}"
							@mouseenter="${this.positionSubmenu}"
							@mouseleave="${this.resetSubmenuPosition}"
							@mousemove=${this.positionSubmenuOffScreen}
							tabIndex="0"
						>
							<slot></slot>
						</div>
						<slot name="submenu"></slot>
					</div>
				`;
			}

			default: {
				return html`
					<div class="${DROPDOWN_ITEM_CLASS}">
						<slot></slot>
					</div>
				`;
			}
		}
	}
};

declare global {
	interface HTMLElementTagNameMap {
		"pn-dropdown-item": DropdownItem;
	}
}
